import { Tooltip } from '@/components';
import {
	AGRI_FRANCHISE_OPTIONS,
	ALL_DOCUMENT_OPTIONS,
	ALLOCATION_RESTRICTED_OPTIONS,
	APPLICANT_TYPE_OPTION,
	AreaRefTableNameEnum,
	BUSINESS_TYPE_OPTIONS,
	EFileType,
	EUpdateAreaEnum,
	EXTEND_STAY_OPTIONS,
	FISHERY_FRANCHISE_OPTIONS,
	FRANCHISE_GRADE_OPTIONS,
	GENDER_OPTION,
	GRADE_OPTIONS,
	INFRASTRUCTURE_OPTIONS,
	MID_TERM_DEPARTURE_OPTIONS,
	NATIONAL_LIST,
	ON_OFF_OPTIONS,
	OWNERSHIP_OPTIONS,
	PRODUCTION_TYPE_AGRICULTURE_OPTIONS,
	PRODUCTION_TYPE_FISHING_OPTIONS,
	SettingUnitMasterInfoFieldTypeEnum,
	STATUS_APPLICATION_TYPE_OPTION,
	STATUS_TYPE_OPTION,
	UpdateFieldNameEnum,
	UpdateHistoryRefTableNameEnum,
	VISA_CODE_OPTION,
	VISA_TIME_OPTIONS,
	YES_NO_OPTIONS,
} from '@/constant';
import { TGetAutocompleteSuggestionResponse } from '@/services';
import { arrayToJson } from '@/utils';

import { DefaultOptionType } from 'antd/es/select';

import { TFunction } from 'i18next';

import { ColumnTypes } from '../common/table/type';
import PreviewHistoryFile from './preview-history-file';
import TargetUpdateHistory from './target-update-history';

export const renderData = (
	value: string,
	record: any,
	historyUpdateOptionJson: {
		[key: string]: DefaultOptionType & {
			optionJson?: any;
			dataType?: SettingUnitMasterInfoFieldTypeEnum;
		};
	},
	t?: TFunction<['updateHistory', 'national'], undefined>,
	autocompleteSuggestion?: TGetAutocompleteSuggestionResponse
) => {
	let label =
		historyUpdateOptionJson[record?.fieldName]?.optionJson?.[value]?.label ??
		value;

	if (label && label?.fileId) {
		return (
			<PreviewHistoryFile
				filesNoAcceptDownloadAndPreview={label?.fileId}
				fileList={[
					{
						sourceType: EFileType.SERVER,
						file: label,
					},
				]}
			/>
		);
	}
	if (label && label?.refTableId) {
		return (
			<TargetUpdateHistory
				key={label?.refTableId}
				record={label}
				autocompleteSuggestion={
					autocompleteSuggestion as TGetAutocompleteSuggestionResponse
				}
			/>
		);
	}

	if (label) {
		switch (record?.fieldName) {
			case UpdateFieldNameEnum.NATIONAL_CODE:
				label = t?.(`national:${value}` as any);
				break;
			case UpdateFieldNameEnum.DETAIL_REASON:
				label = t?.(`updateHistory:reason-detail-options:${value}` as any);
				break;
			case UpdateFieldNameEnum.OCCURING_AREA:
				label = t?.(`updateHistory:occuring-area:${value}` as any);
				break;
			case UpdateFieldNameEnum.VISA_CODE:
				label = t?.(`updateHistory:visa-code-options:${value}` as any);
				break;
			case UpdateFieldNameEnum.PROVIDER_TABLE:
				label = t?.(`updateHistory:provider-table-options:${value}` as any);
				break;
			case UpdateFieldNameEnum.APPLICATION_TYPE:
			case UpdateFieldNameEnum.GENDER:
			case UpdateFieldNameEnum.TYPE:
			case UpdateFieldNameEnum.EXTENSION_PERIOD:
			case UpdateFieldNameEnum.VISA_TIME:
			case UpdateFieldNameEnum.GRADE:
			case UpdateFieldNameEnum.STATUS_ID:
			case UpdateFieldNameEnum.ISSUANCE_STATUS_ID:
			case UpdateFieldNameEnum.REASON:
				label = t?.(label);
				break;
			default:
				break;
		}
	} else {
		label = '-';
	}

	return (
		<Tooltip title={label} destroyTooltipOnHide>
			<div className="truncate">
				{typeof label === 'object' ? label?.name || '-' : label}
			</div>
		</Tooltip>
	);
};

export const getColumns = ({
	historyUpdateOptionJson,
	updateAreaOptionsJson,
	t,
	autocompleteSuggestion,
	isForeignRole,
}: {
	historyUpdateOptionJson: {
		[key: string]: DefaultOptionType & { optionJson?: any };
	};
	updateAreaOptionsJson: {
		[key: string]: DefaultOptionType & { optionJson?: any };
	};
	t: TFunction<['updateHistory', 'national'], undefined>;
	autocompleteSuggestion?: TGetAutocompleteSuggestionResponse;
	isForeignRole?: boolean;
}): ColumnTypes => [
	{
		title: 'NO.',
		dataIndex: 'no',
		className: '!cursor-auto',
	},
	{
		title: t('update-area'),
		dataIndex: 'fieldArea',
		className: '!cursor-auto',
		render: (value: string) => {
			const label = updateAreaOptionsJson[value]?.label ?? value;
			return (
				<Tooltip title={t(label as any)} destroyTooltipOnHide>
					<div className="truncate">{t(label as any)}</div>
				</Tooltip>
			);
		},
	},
	{
		title: t('fieldName'),
		dataIndex: 'fieldName',
		className: '!cursor-auto max-w-[200px]',
		render: (value: string, record: any) => {
			const documentOptionObj = ALL_DOCUMENT_OPTIONS.find(
				(option) => option.value === value
			);
			const documentOption = isForeignRole
				? documentOptionObj?.labelEn
				: documentOptionObj?.label;

			const label =
				record?.fieldArea === EUpdateAreaEnum.DOCUMENT_INFO
					? documentOption
					: historyUpdateOptionJson[value]?.label ?? value;
			return (
				<Tooltip title={t(label as any)} destroyTooltipOnHide>
					<div className="truncate">{t(label as any)}</div>
				</Tooltip>
			);
		},
	},
	{
		title: t('prevData'),
		dataIndex: 'prevData',
		width: '25%',
		className: '!cursor-auto max-w-[400px]',
		render: (value: string, record: any) => {
			return renderData(
				value,
				record,
				historyUpdateOptionJson,
				t,
				autocompleteSuggestion
			);
		},
	},
	{
		title: t('afterData'),
		dataIndex: 'afterData',
		width: '25%',
		className: '!cursor-auto max-w-[400px]',
		render: (value: string, record: any) => {
			return renderData(
				value,
				record,
				historyUpdateOptionJson,
				t,
				autocompleteSuggestion
			);
		},
	},
	{
		title: t('registName'),
		dataIndex: 'registName',
		className: '!cursor-auto max-w-[200px]',
		render: (value: string) => {
			return (
				<Tooltip title={value} destroyTooltipOnHide>
					<div className="truncate">{value}</div>
				</Tooltip>
			);
		},
	},
	{
		title: t('registDt'),
		dataIndex: 'registDt',
		className: '!cursor-auto max-w-[200px]',
		render: (value: string) => {
			return (
				<Tooltip title={value} destroyTooltipOnHide>
					<div className="truncate">{value}</div>
				</Tooltip>
			);
		},
	},
];

const UPDATE_HISTORY_APPLICANT_OPTIONS: DefaultOptionType[] = [
	{
		value: '',
		label: 'updateHistory:all-update-history',
		isForeignRole: true,
	},
	{
		value: UpdateFieldNameEnum.DISTRICT_ID,
		label: 'updateHistory:applicant-options.district',
	},
	{
		value: UpdateFieldNameEnum.FIRST_NAME,
		label: 'updateHistory:applicant-options.first-name',
		isForeignRole: true,
	},
	{
		value: UpdateFieldNameEnum.LAST_NAME,
		label: 'updateHistory:applicant-options.last-name',
		isForeignRole: true,
	},
	{
		value: UpdateFieldNameEnum.BIRTH_DATE,
		label: 'updateHistory:applicant-options.birth-date',
		dataType: SettingUnitMasterInfoFieldTypeEnum.DATE,
		isForeignRole: true,
	},
	{
		value: UpdateFieldNameEnum.GENDER,
		label: 'updateHistory:applicant-options.gender',
		optionJson: arrayToJson(GENDER_OPTION, 'value'),
		isForeignRole: true,
	},
	{
		value: UpdateFieldNameEnum.HEIGHT,
		label: 'updateHistory:applicant-options.height',
		isForeignRole: true,
	},
	{
		value: UpdateFieldNameEnum.WEIGHT,
		label: 'updateHistory:applicant-options.weight',
		isForeignRole: true,
	},
	{
		value: UpdateFieldNameEnum.PHONE,
		label: 'updateHistory:applicant-options.phone',
	},
	{
		value: UpdateFieldNameEnum.HOMETOWN_ADDR,
		label: 'updateHistory:applicant-options.hometown-addr',
		isForeignRole: true,
	},
	{
		value: UpdateFieldNameEnum.HOMETOWN_PHONE,
		label: 'updateHistory:applicant-options.hometown-phone',
		isForeignRole: true,
	},
	{
		value: UpdateFieldNameEnum.NATIONAL_CODE,
		label: 'updateHistory:applicant-options.national-code',
		optionJson: arrayToJson(NATIONAL_LIST, 'value'),
		isForeignRole: true,
	},
	{
		value: UpdateFieldNameEnum.PASSPORT_NO,
		label: 'updateHistory:applicant-options.passport-no',
		isForeignRole: true,
	},
	{
		value: UpdateFieldNameEnum.PASSPORT_ISSUE_DATE,
		label: 'updateHistory:applicant-options.passport-issue-date',
		isForeignRole: true,
	},
	{
		value: UpdateFieldNameEnum.PASSPORT_EXPIRE_DATE,
		label: 'updateHistory:applicant-options.passport-expire-date',
		isForeignRole: true,
	},
	{
		value: UpdateFieldNameEnum.APPLICATION_TYPE,
		label: 'updateHistory:applicant-options.application-type',
		optionJson: arrayToJson(APPLICANT_TYPE_OPTION, 'value'),
		isForeignRole: true,
	},
	{
		value: UpdateFieldNameEnum.IS_RE_ENTRY,
		label: 'updateHistory:applicant-options.is-re-entry',
		optionJson: arrayToJson(YES_NO_OPTIONS, 'value'),
	},
	{
		value: UpdateFieldNameEnum.RE_ENTRY_COUNT,
		label: 'updateHistory:applicant-options.re-entry-count',
	},
	{
		value: UpdateFieldNameEnum.IS_PUBLIC,
		label: 'updateHistory:applicant-options.is-public',
		optionJson: arrayToJson(YES_NO_OPTIONS, 'value'),
	},
	{
		value: UpdateFieldNameEnum.PUBLIC_FRANCHISE_ID,
		label: 'updateHistory:applicant-options.public-franchise-id',
	},
	{
		value: UpdateFieldNameEnum.GRADE,
		label: 'updateHistory:applicant-options.grade',
		optionJson: arrayToJson(GRADE_OPTIONS, 'value'),
	},
	{
		value: UpdateFieldNameEnum.STATUS_ID,
		label: 'updateHistory:applicant-options.status',
		optionJson: arrayToJson(STATUS_TYPE_OPTION, 'value'),
	},
	{
		value: UpdateFieldNameEnum.ISSUANCE_STATUS_ID,
		label: 'updateHistory:applicant-options.issuance-status',
		optionJson: arrayToJson(STATUS_APPLICATION_TYPE_OPTION, 'value'),
	},
	{
		value: UpdateFieldNameEnum.VISA_ISSUANCE_CONFIRM_NO,
		label: 'updateHistory:applicant-options.visa-issuance-confirm-no',
	},
	{
		value: UpdateFieldNameEnum.VISA_NO,
		label: 'updateHistory:applicant-options.visa-no',
	},
	{
		value: UpdateFieldNameEnum.FOREIGNER_NO,
		label: 'updateHistory:applicant-options.foreigner-no',
	},
	{
		value: UpdateFieldNameEnum.ENTRY_DATE,
		label: 'updateHistory:applicant-options.entry-date',
	},
	{
		value: UpdateFieldNameEnum.DEPARTURE_DATE,
		label: 'updateHistory:applicant-options.departure-date',
	},
	{
		value: UpdateFieldNameEnum.VISA_CODE,
		label: 'updateHistory:applicant-options.visa-code',
		optionJson: arrayToJson(VISA_CODE_OPTION, 'value'),
	},
	{
		value: UpdateFieldNameEnum.MEMO,
		label: 'updateHistory:applicant-options.memo',
	},
	{
		value: UpdateFieldNameEnum.VISA_TIME,
		label: 'updateHistory:applicant-options.visa-time',
		optionJson: arrayToJson(VISA_TIME_OPTIONS, 'value'),
	},
	{
		value: UpdateFieldNameEnum.EXTENSION_PERIOD,
		label: 'updateHistory:applicant-options.extension-period',
		optionJson: arrayToJson(EXTEND_STAY_OPTIONS, 'value'),
	},
	{
		value: UpdateFieldNameEnum.MID_TERM_DEPARTURE,
		label: 'updateHistory:applicant-options.mid-term-departure',
		optionJson: arrayToJson(MID_TERM_DEPARTURE_OPTIONS, 'value'),
	},
	{
		value: UpdateFieldNameEnum.UNIT_ID,
		label: 'updateHistory:applicant-options.unit-id',
		isForeignRole: true,
	},
];

const UPDATE_HISTORY_FRANCHISE_OPTIONS: DefaultOptionType[] = [
	{
		value: '',
		label: '수정 사항 전체',
	},
	{
		value: UpdateFieldNameEnum.EMAIL,
		label: '이메일',
	},
	{
		value: UpdateFieldNameEnum.NAME,
		label: '사업장',
	},
	{
		value: UpdateFieldNameEnum.PHONE,
		label: '사업장 전화번호',
	},
	{
		value: UpdateFieldNameEnum.TYPE,
		label: '사업장 구분',
		optionJson: arrayToJson(BUSINESS_TYPE_OPTIONS, 'value'),
	},
	{
		value: UpdateFieldNameEnum.TYPE_DETAIL,
		label: '사업장 상세 구분',
		optionJson: arrayToJson(
			[...AGRI_FRANCHISE_OPTIONS, ...FISHERY_FRANCHISE_OPTIONS],
			'value'
		),
	},
	{
		value: UpdateFieldNameEnum.PRODUCTION_TYPE,
		label: '작물/수산물 종류',
		optionJson: arrayToJson(
			[
				...PRODUCTION_TYPE_AGRICULTURE_OPTIONS,
				...PRODUCTION_TYPE_FISHING_OPTIONS,
			],
			'value'
		),
	},
	{
		value: UpdateFieldNameEnum.PRODUCTION_TYPE_DETAIL,
		label: '작물/수산물 상세',
	},
	{
		value: UpdateFieldNameEnum.PRODUCTION_SCALE,
		label: '재배면적',
	},
	{
		value: UpdateFieldNameEnum.MAX_MANPOWER_ALLOWED,
		label: '최대 허용 인원',
	},
	{
		value: UpdateFieldNameEnum.DISTRICT_ID,
		label: '읍/면',
	},
	{
		value: UpdateFieldNameEnum.ADDRESS,
		label: '사업장 주소',
	},
	{
		value: UpdateFieldNameEnum.EMPLOYER_NAME,
		label: '고용주',
	},
	{
		value: UpdateFieldNameEnum.EMPLOYER_BIRTH_DATE,
		label: '고용주 생년월일',
		dataType: SettingUnitMasterInfoFieldTypeEnum.DATE,
	},
	{
		value: UpdateFieldNameEnum.EMPLOYER_PHONE,
		label: '고용주 전화번호',
	},
	{
		value: UpdateFieldNameEnum.IS_PUBLIC,
		label: '공공형',
		optionJson: arrayToJson(YES_NO_OPTIONS, 'value'),
	},
	{
		value: UpdateFieldNameEnum.PUBLIC_FRANCHISE_ID,
		label: '공공형 사업자(농협)',
	},
	{
		value: UpdateFieldNameEnum.BUSINESS_REGIST_NO,
		label: '농어업경영체 등록번호',
	},
	{
		value: UpdateFieldNameEnum.CORPORATE_REGIST_NO,
		label: '법인·조합 등록번호',
	},

	{
		value: UpdateFieldNameEnum.PENALTY_POINTS_ACCUMULATED,
		label: '누적 벌점',
	},
	{
		value: UpdateFieldNameEnum.IS_ALLOCATION_RESTRICTED,
		label: '배정 제한 대상 여부',
		optionJson: arrayToJson(ALLOCATION_RESTRICTED_OPTIONS, 'value'),
	},
	{
		value: UpdateFieldNameEnum.WORK_START_TIME,
		label: '기본 근무 시작 시간',
	},
	{
		value: UpdateFieldNameEnum.WORK_END_TIME,
		label: '기본 근무 끝 시간',
	},
	{
		value: UpdateFieldNameEnum.MONTHLY_WORKING_HOURS,
		label: '총 기본 근무 시간',
	},
	{
		value: UpdateFieldNameEnum.DAILY_BREAKS_NUMBER,
		label: '휴게시간 일',
	},
	{
		value: UpdateFieldNameEnum.DAILY_BREAKS_HOUR,
		label: '휴게시간 시간',
	},
	{
		value: UpdateFieldNameEnum.DAILY_BREAKS_MIN,
		label: '휴게시간 분',
	},
	{
		value: UpdateFieldNameEnum.GRADE_A_DAILY_WAGE,
		label: 'A등급 일급',
	},
	{
		value: UpdateFieldNameEnum.GRADE_B_DAILY_WAGE,
		label: 'B등급 일급',
	},
	{
		value: UpdateFieldNameEnum.GRADE_C_DAILY_WAGE,
		label: 'C등급 일급',
	},
	{
		value: UpdateFieldNameEnum.DEFAULT_GRADE,
		label: '기본 등급',
		optionJson: arrayToJson(FRANCHISE_GRADE_OPTIONS, 'value'),
	},
	{
		value: UpdateFieldNameEnum.MEMO,
		label: '특이사항',
	},
];

const UPDATE_HISTORY_LODGING_OPTIONS: DefaultOptionType[] = [
	{
		value: '',
		label: '수정 사항 전체',
	},
	{
		value: UpdateFieldNameEnum.ADDRESS,
		label: '숙소 주소',
	},
	{
		value: UpdateFieldNameEnum.DISTRICT_ID,
		label: '읍/면',
	},
	{
		value: UpdateFieldNameEnum.OWNERSHIP_TYPE,
		label: '소유형태',
		optionJson: arrayToJson(OWNERSHIP_OPTIONS, 'value'),
	},
	{
		value: UpdateFieldNameEnum.FACILITY_TYPE,
		label: '시설종류',
		optionJson: arrayToJson(INFRASTRUCTURE_OPTIONS, 'value'),
	},
	{
		value: UpdateFieldNameEnum.PROVIDED_DATE,
		label: '숙소 제공일',
	},
	{
		value: UpdateFieldNameEnum.INSPECTION_DATE,
		label: '숙소 점검일',
	},
	{
		value: UpdateFieldNameEnum.PROVIDER_TABLE,
		label: '제공자 정보: 구분',
	},
	{
		value: UpdateFieldNameEnum.PROVIDER_ID,
		label: '제공자 정보: 이름',
	},
	{
		value: UpdateFieldNameEnum.PROVIDER_NAME,
		label: '제공자 정보: 이름',
	},
	{
		value: UpdateFieldNameEnum.PROVIDER_NATIONAL_CODE,
		label: '제공자 정보: 국적',
		optionJson: arrayToJson(NATIONAL_LIST, 'value'),
	},
	{
		value: UpdateFieldNameEnum.PROVIDER_PHONE,
		label: '제공자 정보: 전화번호',
	},
	{
		value: UpdateFieldNameEnum.PROVIDER_ADDRESS,
		label: '제공자 정보:  주소',
	},
	{
		value: UpdateFieldNameEnum.MEMO,
		label: '특이사항',
	},
	{
		value: UpdateFieldNameEnum.LODGING_COST,
		label: '숙식 비용: 숙박비',
	},
	{
		value: UpdateFieldNameEnum.MEAL_COST,
		label: '숙식 비용: 식비',
	},
];

const UPDATE_HISTORY_RECOMMENDER_OPTIONS: DefaultOptionType[] = [
	{
		value: '',
		label: '수정 사항 전체',
	},
	{
		value: UpdateFieldNameEnum.NAME,
		label: '추천자 정보: 추천자',
	},
	{
		value: UpdateFieldNameEnum.ENG_NAME,
		label: '결혼이민자 추천자 정보: 추천자 영문 이름',
	},
	{
		value: UpdateFieldNameEnum.PROVIDER_NATIONAL_CODE,
		label: '결혼이민자 추천자 정보: 이전 국적',
		optionJson: arrayToJson(NATIONAL_LIST, 'value'),
	},
	{
		value: UpdateFieldNameEnum.VISA_CODE,
		label: '결혼이민자 추천자 정보: 체류자격',
	},
	{
		value: UpdateFieldNameEnum.ADDRESS,
		label: '결혼이민자 추천자 정보: 주소',
	},
	{
		value: UpdateFieldNameEnum.BIRTH_DATE,
		label: '결혼이민자 추천자 정보: 생년월일',
		dataType: SettingUnitMasterInfoFieldTypeEnum.DATE,
	},
	{
		value: UpdateFieldNameEnum.PHONE,
		label: '결혼이민자 추천자 정보: 전화번호',
	},
	{
		value: UpdateFieldNameEnum.EMAIL,
		label: '결혼이민자 추천자 정보: 이메일',
	},
	{
		value: UpdateFieldNameEnum.SUB_TYPE,
		label: '배우자 정보: 구분',
	},
	{
		value: UpdateFieldNameEnum.FRANCHISE_ID,
		label: '배우자 정보: 사업장명',
	},
	{
		value: UpdateFieldNameEnum.SPOUSE_NAME,
		label: '배우자 정보: 배우자',
	},
	{
		value: UpdateFieldNameEnum.SPOUSE_PHONE,
		label: '배우자 정보: 배우자 정보 전화번호',
	},
	{
		value: UpdateFieldNameEnum.MEMO,
		label: '특이사항',
	},
];

const UPDATE_HISTORY_PROGRAM_OPTIONS: DefaultOptionType[] = [
	{
		value: '',
		label: 'updateHistory:all-update-history',
		isForeignRole: true,
	},
	{
		value: UpdateFieldNameEnum.NAME,
		label: 'updateHistory:program:name',
		isForeignRole: true,
	},
	{
		value: UpdateFieldNameEnum.START_DATE,
		label: 'updateHistory:program:startDate',
		isForeignRole: true,
	},
	{
		value: UpdateFieldNameEnum.END_DATE,
		label: 'updateHistory:program:endDate',
		isForeignRole: true,
	},
	{
		value: UpdateFieldNameEnum.MEMO,
		label: 'updateHistory:program:memo',
		isForeignRole: true,
	},
];

const UPDATE_HISTORY_UNIT_OPTIONS: DefaultOptionType[] = [
	{
		value: '',
		label: '수정 사항 전체',
	},
	{
		value: UpdateFieldNameEnum.TYPE,
		label: '구분',
	},
	{
		value: UpdateFieldNameEnum.NAME,
		label: '사업체',
	},
	{
		value: UpdateFieldNameEnum.CITY_PROVINCE,
		label: '광역시/도',
	},
	{
		value: UpdateFieldNameEnum.COUNTY_DISTRICT,
		label: '시/군/구',
	},
	{
		value: UpdateFieldNameEnum.PHONE,
		label: '사업체 전화번호',
	},
	{
		value: UpdateFieldNameEnum.BUSINESS_REGIST_NO,
		label: '사업자 등록번호',
	},
	{
		value: UpdateFieldNameEnum.CORPORATE_REGIST_NO,
		label: '법인 등록번호',
	},
	{
		value: UpdateFieldNameEnum.MANAGER_NAME,
		label: '담당자',
	},
	{
		value: UpdateFieldNameEnum.MANAGER_PHONE,
		label: '담당자 전화번호',
	},
	{
		value: UpdateFieldNameEnum.ZIPCODE,
		label: '우편번호',
	},
	{
		value: UpdateFieldNameEnum.ADDRESS,
		label: '숙소 주소',
	},
	{
		value: UpdateFieldNameEnum.ADDRESS_DETAIL,
		label: '상세 사업장 주소',
	},
	{
		value: UpdateFieldNameEnum.COMPANY_SEAL_FILE_ID,
		label: '직인',
		type: EUpdateAreaEnum.SEAL_INFO,
	},
	// {
	// 	value: UpdateFieldNameEnum.NAME_PLATE_TITLE,
	// 	label: '명찰 제목',
	// 	type: EUpdateAreaEnum.NAME_PLATE_INFO,
	// },
	{
		value: UpdateFieldNameEnum.NAME_PLATE_CONTENT,
		label: '명찰 뒷면 내용',
		type: EUpdateAreaEnum.NAME_PLATE_INFO,
	},
	{
		value: UpdateFieldNameEnum.IS_ACTIVATED_APPLICANT_NOTI,
		label: '알림_근로자 공지사항',
		optionJson: arrayToJson(ON_OFF_OPTIONS, 'value'),
		type: EUpdateAreaEnum.NOTIFICATION_CONTROL,
	},
	{
		value: UpdateFieldNameEnum.IS_ACTIVATED_FRANCHISE_NOTI,
		label: '알림_사업장 공지사항',
		optionJson: arrayToJson(ON_OFF_OPTIONS, 'value'),
		type: EUpdateAreaEnum.NOTIFICATION_CONTROL,
	},
	{
		value: UpdateFieldNameEnum.IS_ACTIVATED_EXP_APPLICANT_SMS,
		label: '문자_근로자 출국 예정',
		optionJson: arrayToJson(ON_OFF_OPTIONS, 'value'),
		type: EUpdateAreaEnum.NOTIFICATION_CONTROL,
	},
	{
		value: UpdateFieldNameEnum.IS_ACTIVATED_EXP_APPLICANT_NOTI,
		label: '알림_근로자 출국 예정',
		optionJson: arrayToJson(ON_OFF_OPTIONS, 'value'),
		type: EUpdateAreaEnum.NOTIFICATION_CONTROL,
	},
	{
		value: UpdateFieldNameEnum.IP_ADDRESS,
		label: '허용 IP',
	},
	{
		value: UpdateFieldNameEnum.IS_ACTIVE,
		label: '도메인 활성화',
		optionJson: arrayToJson(ON_OFF_OPTIONS, 'value'),
	},
];

const UPDATE_HISTORY_FOREIGN_UNIT_OPTIONS: DefaultOptionType[] = [
	{
		value: '',
		label: 'updateHistory:all-update-history',
	},
	{
		value: UpdateFieldNameEnum.NATIONAL_CODE,
		label: 'updateHistory:unit-options.national',
	},
	{
		value: UpdateFieldNameEnum.TYPE,
		label: 'updateHistory:unit-options.type',
	},
	{
		value: UpdateFieldNameEnum.NAME,
		label: 'updateHistory:unit-options.name',
	},
	{
		value: UpdateFieldNameEnum.PHONE,
		label: 'updateHistory:unit-options.phone',
	},
	{
		value: UpdateFieldNameEnum.FOREIGN_ADDRESS,
		label: 'updateHistory:unit-options.address-detail',
	},

	{
		value: UpdateFieldNameEnum.MANAGER_NAME,
		label: 'updateHistory:unit-options.manager-name',
	},
	{
		value: UpdateFieldNameEnum.MANAGER_PHONE,
		label: 'updateHistory:unit-options.manager-phone',
	},
	{
		value: UpdateFieldNameEnum.IP_ADDRESS,
		label: 'updateHistory:unit-options.ip-address',
	},
];

export const UPDATE_HISTORY_ASSIGNMENT_FRANCHISE_OPTIONS: DefaultOptionType[] =
	[
		{
			value: '',
			label: '수정 사항 전체',
		},
		{
			value: UpdateFieldNameEnum.FRANCHISE_ID,
			label: '배정 사업장',
		},
		{
			value: UpdateFieldNameEnum.REQUEST_APPLICANT,
			label: '배정 기간/인원: 요청 인원',
		},
		{
			value: UpdateFieldNameEnum.START_DATE,
			label: '배정 기간/인원: 배정 기간 (시작일)',
		},
		{
			value: UpdateFieldNameEnum.END_DATE,
			label: '배정 기간/인원: 배정 기간 (종료일)',
		},
		{
			value: UpdateFieldNameEnum.MEMO,
			label: '특이사항',
		},
		{
			value: UpdateFieldNameEnum.RELAX_TIME,
			label: '휴게시간',
		},
		{
			value: UpdateFieldNameEnum.START_TIME,
			label: '배정 일자/인원/시간: 근무시간 (근무 시간 시작)',
		},
		{
			value: UpdateFieldNameEnum.END_TIME,
			label: '배정 일자/인원/시간: 근무시간 (근무 시간 종료)',
		},
	];

export const UPDATE_HISTORY_PUBLIC_ASSIGNMENT_FRANCHISE_OPTIONS: DefaultOptionType[] =
	[
		{
			value: '',
			label: '수정 사항 전체',
		},
		{
			value: UpdateFieldNameEnum.REQUEST_APPLICANT,
			label: '배정 일자/인원/시간: 요청 인원',
		},
		{
			value: UpdateFieldNameEnum.START_DATE,
			label: '배정 일자/인원/시간: 배정 일자',
		},
		{
			value: UpdateFieldNameEnum.MEMO,
			label: '특이사항',
		},
		{
			value: UpdateFieldNameEnum.RELAX_TIME,
			label: '배정 일자/인원/시간: 휴게시간',
		},
		{
			value: UpdateFieldNameEnum.START_TIME,
			label: '배정 일자/인원/시간: 근무시간 (근무 시간 시작)',
		},
		{
			value: UpdateFieldNameEnum.END_TIME,
			label: '배정 일자/인원/시간: 근무시간 (근무 시간 종료)',
		},
	];

export const UPDATE_HISTORY_WORK_CHANGE_PLACE_OPTIONS: DefaultOptionType[] = [
	{
		value: '',
		label: '수정 사항 전체',
	},
	{
		value: UpdateFieldNameEnum.ASSIGNMENT_DATE,
		label: '근로자 정보: 원 근무 종료일',
	},
	{
		value: UpdateFieldNameEnum.REASON,
		label: '변경 사유: 변경 사유',
	},
	{
		value: UpdateFieldNameEnum.DETAIL_REASON,
		label: '변경 사휴: 세부 사유',
	},
	{
		value: UpdateFieldNameEnum.MEMO,
		label: '특이사항',
	},
];

export const UPDATE_HISTORY_UNAUTHORIZED_DEPARTURE_OPTIONS: DefaultOptionType[] =
	[
		{
			value: '',
			label: '수정 사항 전체',
		},
		{
			value: UpdateFieldNameEnum.APPLICANT_ID,
			label: '근로자 정보',
		},
		{
			value: UpdateFieldNameEnum.OCCURING_AREA,
			label: '변경: 발생 장소(구분)',
		},
		{
			value: UpdateFieldNameEnum.OCCURING_AREA_DETAIL,
			label: '변경: 발생 장소(상세)',
		},
		{
			value: UpdateFieldNameEnum.REASON,
			label: '변경: 발생 사유',
		},
		{
			value: UpdateFieldNameEnum.OCCURING_DATE,
			label: '변경: 발생 일자',
		},
		{
			value: UpdateFieldNameEnum.MEMO,
			label: '특이사항',
		},
	];

export const updateHistoryOptionsMap: {
	[key in UpdateHistoryRefTableNameEnum]: DefaultOptionType[];
} = {
	[UpdateHistoryRefTableNameEnum.USER]: [],
	[UpdateHistoryRefTableNameEnum.UNIT]: UPDATE_HISTORY_UNIT_OPTIONS,
	[UpdateHistoryRefTableNameEnum.FOREIGN_UNIT]:
		UPDATE_HISTORY_FOREIGN_UNIT_OPTIONS,
	[UpdateHistoryRefTableNameEnum.FILE]: [],
	[UpdateHistoryRefTableNameEnum.FILE_MAPPING]: [],
	[UpdateHistoryRefTableNameEnum.UPDATE_HISTORY]: [],
	[UpdateHistoryRefTableNameEnum.UNIT_DISTRICT]: [],
	[UpdateHistoryRefTableNameEnum.UNIT_APPLICANT_STATUS]: [],
	[UpdateHistoryRefTableNameEnum.UNIT_OPTIONAL_FIELD]: [],
	[UpdateHistoryRefTableNameEnum.APPLICANT]: UPDATE_HISTORY_APPLICANT_OPTIONS,
	[UpdateHistoryRefTableNameEnum.APPLICANT_PROGRAM]: [],
	[UpdateHistoryRefTableNameEnum.FRANCHISE]: UPDATE_HISTORY_FRANCHISE_OPTIONS,
	[UpdateHistoryRefTableNameEnum.ASSIGNMENT_FRANCHISE]:
		UPDATE_HISTORY_ASSIGNMENT_FRANCHISE_OPTIONS,
	[UpdateHistoryRefTableNameEnum.PUBLIC_ASSIGNMENT_FRANCHISE]:
		UPDATE_HISTORY_PUBLIC_ASSIGNMENT_FRANCHISE_OPTIONS,
	[UpdateHistoryRefTableNameEnum.ASSIGNMENT_APPLICANT]: [],
	[UpdateHistoryRefTableNameEnum.LODGING]: UPDATE_HISTORY_LODGING_OPTIONS,
	[UpdateHistoryRefTableNameEnum.FRANCHISE_PROGRAM]: [],
	[UpdateHistoryRefTableNameEnum.PROGRAM]: UPDATE_HISTORY_PROGRAM_OPTIONS,
	[UpdateHistoryRefTableNameEnum.EMAIL]: [],
	[UpdateHistoryRefTableNameEnum.EXTENSION_STAY]: [],
	[UpdateHistoryRefTableNameEnum.EXTENSION_STAY_APPLICANT]: [],
	[UpdateHistoryRefTableNameEnum.CHANGE_WORK_PLACE]:
		UPDATE_HISTORY_WORK_CHANGE_PLACE_OPTIONS,
	[UpdateHistoryRefTableNameEnum.UNAUTHORIZED_DEPARTURE]:
		UPDATE_HISTORY_UNAUTHORIZED_DEPARTURE_OPTIONS,
	[UpdateHistoryRefTableNameEnum.APPLICANT_SALARY]: [],
	[UpdateHistoryRefTableNameEnum.NOTICE]: [],
	[UpdateHistoryRefTableNameEnum.INQUIRY_BOARD]: [],
	[UpdateHistoryRefTableNameEnum.RECOMMENDER]:
		UPDATE_HISTORY_RECOMMENDER_OPTIONS,
	[UpdateHistoryRefTableNameEnum.APPLICANT_PROCESSING]: [],
	[UpdateHistoryRefTableNameEnum.WORKER_PROGRAM]: [],
};

export const AREA_UNIT_OPTIONS: DefaultOptionType[] = [
	{
		value: '',
		label: 'updateHistory:update-area-options.all',
	},
	{
		value: EUpdateAreaEnum.BASIC_INFO,
		label: 'updateHistory:update-area-options.basic-info',
	},
	{
		value: EUpdateAreaEnum.SEAL_INFO,
		label: 'updateHistory:update-area-options.seal-info',
	},
	{
		value: EUpdateAreaEnum.DOCUMENT_INFO,
		label: 'updateHistory:update-area-options.document-info',
	},
	{
		value: EUpdateAreaEnum.NOTIFICATION_CONTROL,
		label: 'updateHistory:update-area-options.notification-control',
	},
	{
		value: EUpdateAreaEnum.NAME_PLATE_INFO,
		label: 'updateHistory:update-area-options.name-plate-info',
	},
];

export const AREA_APPLICANT_OPTIONS: DefaultOptionType[] = [
	{
		value: '',
		label: 'updateHistory:update-area-options.all',
	},
	{
		value: EUpdateAreaEnum.BASIC_INFO,
		label: 'updateHistory:update-area-options.basic-info',
	},
	{
		value: EUpdateAreaEnum.DOCUMENT_INFO,
		label: 'updateHistory:update-area-options.document-info',
	},
];

export const AREA_FRANCHISE_OPTIONS: DefaultOptionType[] = [
	{
		value: '',
		label: 'updateHistory:update-area-options.all',
	},
	{
		value: EUpdateAreaEnum.BASIC_INFO,
		label: 'updateHistory:update-area-options.basic-info',
	},
	{
		value: EUpdateAreaEnum.DOCUMENT_INFO,
		label: 'updateHistory:update-area-options.document-info',
	},
];

export const AREA_LODGING_OPTIONS: DefaultOptionType[] = [
	{
		value: '',
		label: 'updateHistory:update-area-options.all',
	},
	{
		value: EUpdateAreaEnum.BASIC_INFO,
		label: 'updateHistory:update-area-options.basic-info',
	},
	{
		value: EUpdateAreaEnum.DOCUMENT_INFO,
		label: 'updateHistory:update-area-options.document-info',
	},
];

export const AREA_RECOMMENDER_OPTIONS: DefaultOptionType[] = [
	{
		value: '',
		label: 'updateHistory:update-area-options.all',
	},
	{
		value: EUpdateAreaEnum.BASIC_INFO,
		label: 'updateHistory:update-area-options.basic-info',
	},
	{
		value: EUpdateAreaEnum.DOCUMENT_INFO,
		label: 'updateHistory:update-area-options.document-info',
	},
];

export const AREA_ASSIGNMENT_FRANCHISE_OPTIONS: DefaultOptionType[] = [
	{
		value: '',
		label: 'updateHistory:update-area-options.all',
	},
	{
		value: EUpdateAreaEnum.BASIC_INFO,
		label: 'updateHistory:update-area-options.basic-info',
	},
];

export const AREA_WORK_CHANGE_PLACE_OPTIONS: DefaultOptionType[] = [
	{
		value: '',
		label: 'updateHistory:update-area-options.all',
	},
	{
		value: EUpdateAreaEnum.BASIC_INFO,
		label: 'updateHistory:update-area-options.basic-info',
	},
	{
		value: EUpdateAreaEnum.DOCUMENT_INFO,
		label: 'updateHistory:update-area-options.document-info',
	},
];

export const AREA_UNAUTHORIZED_DEPARTURE_OPTIONS: DefaultOptionType[] = [
	{
		value: '',
		label: 'updateHistory:update-area-options.all',
	},
	{
		value: EUpdateAreaEnum.BASIC_INFO,
		label: 'updateHistory:update-area-options.basic-info',
	},
	{
		value: EUpdateAreaEnum.DOCUMENT_INFO,
		label: 'updateHistory:update-area-options.document-info',
	},
];

export const AREA_WORKER_PROGRAM_HISTORY_OPTIONS: DefaultOptionType[] = [
	{
		value: '',
		label: 'updateHistory:update-area-options.all',
	},
	{
		value: EUpdateAreaEnum.BASIC_INFO,
		label: 'updateHistory:update-area-options.basic-info',
	},
	{
		value: EUpdateAreaEnum.DOCUMENT_INFO,
		label: 'updateHistory:update-area-options.document-info',
	},
];

export const updateAreaOptionsMap: {
	[key in AreaRefTableNameEnum]: DefaultOptionType[];
} = {
	[AreaRefTableNameEnum.UNIT]: AREA_UNIT_OPTIONS,
	[AreaRefTableNameEnum.APPLICANT]: AREA_APPLICANT_OPTIONS,
	[AreaRefTableNameEnum.FRANCHISE]: AREA_FRANCHISE_OPTIONS,
	[AreaRefTableNameEnum.ASSIGNMENT_FRANCHISE]: [],
	[AreaRefTableNameEnum.LODGING]: AREA_LODGING_OPTIONS,
	[AreaRefTableNameEnum.FRANCHISE_PROGRAM]: [],
	[AreaRefTableNameEnum.RECOMMENDER]: AREA_RECOMMENDER_OPTIONS,
	[AreaRefTableNameEnum.WORK_CHANGE_PLACE]: AREA_WORK_CHANGE_PLACE_OPTIONS,
	[AreaRefTableNameEnum.UNAUTHORIZED_DEPARTURE]:
		AREA_UNAUTHORIZED_DEPARTURE_OPTIONS,
	[AreaRefTableNameEnum.PROGRAM]: AREA_WORKER_PROGRAM_HISTORY_OPTIONS,
	[AreaRefTableNameEnum.OTHER]: [],
};

export const updateAreaAllOptions = [
	{
		value: '',
		label: 'updateHistory:update-area-options.all',
	},
];
